"use client"

import { useTranslation } from "react-i18next"
import { CheckCircle2, User, UserPlus } from "lucide-react"
import { useBookingStore } from "@/store/booking-store"
import { useState, useEffect } from "react"

export default function BookingModeSelection({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation()
  const { bookingType, setBookingState } = useBookingStore()
  // Initialize as null so it doesn't show previous selection on first render
  const [localType, setLocalType] = useState<"self" | "other" | null>(null)

  // Reset booking type in global store on mount
  useEffect(() => {
    setBookingState({ bookingType: undefined })
  }, [setBookingState])

  const handleSelect = (type: "self" | "other") => {
    setLocalType(type)
    setBookingState({ bookingType: type })
    // Automatic continuation
    onContinue()
  }

  // Remove manual continue handler and button
  return (
    <div className="animate-fade-in-up w-full"> 
      <h2 className="text-lg font-bold text-gray-900 mb-2 text-start">{t("whoIsAppointmentfor")}</h2>
      <p className="text-gray-500 text-start mb-10 text-sm  mx-auto">{t("pleaseSelectBookingFor")}</p>

      <div className="grid grid-cols-2 gap-2 md:gap-8 max-w-3xl mx-auto mb-8 px-2 md:px-0">
        {/* Self Option */}
        <button
          onClick={() => handleSelect("self")}
          className={`
            relative flex flex-col items-center px-3 py-4 md:px-8 md:py-8 rounded-2xl md:rounded-3xl border-2 transition-all duration-300 group
            ${localType === "self" 
              ? "border-pink-500 bg-pink-50 shadow-lg shadow-pink-100/50 scale-[1.02]" 
              : "border-gray-100 bg-white shadow-xs  hover:border-pink-200 hover:bg-gray-50 hover:-translate-y-1 hover:shadow-lg"
            }
          `}
        >
          <div className={`
            p-3 md:p-5 rounded-full mb-3 md:mb-5 transition-transform duration-300 group-hover:scale-110
            ${localType === "self" ? "bg-pink-100 text-pink-600" : "bg-pink-50 text-pink-500 group-hover:bg-pink-100 group-hover:text-pink-600"}
          `}>
            <User className="w-6 h-6 md:w-10 md:h-10" strokeWidth={1.5} />
          </div>
          <h3 className="text-md font-bold text-gray-900 mb-1 md:mb-2">{t("Booking for self")}</h3>
          <p className="text-xs md:text-sm text-gray-500 text-center leading-relaxed w-full md:max-w-[80%]">{t("bookForMyselfDesc")}</p>
          
        </button>

        {/* Other Option */}
        <button
          onClick={() => handleSelect("other")}
          className={`
            relative flex flex-col items-center px-3 py-4 md:px-8 md:py-8 rounded-2xl md:rounded-3xl border-2 transition-all duration-300 group
            ${localType === "other" 
              ? "border-pink-500 bg-pink-50 shadow-lg shadow-pink-100/50 scale-[1.02]" 
              : "border-gray-100 bg-white shadow-xs hover:border-pink-200 hover:bg-gray-50 hover:-translate-y-1 hover:shadow-lg"
            }
          `}
        >
          <div className={`
            p-3 md:p-5 rounded-full mb-3 md:mb-5 transition-transform duration-300 group-hover:scale-110
            ${localType === "other" ? "bg-pink-100 text-pink-600" : "bg-pink-50 text-pink-500 group-hover:bg-pink-100 group-hover:text-pink-600"}
          `}>
            <UserPlus className="w-6 h-6 md:w-10 md:h-10" strokeWidth={1.5} />
          </div>
          <h3 className="text-md md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t("Booking for other")}</h3>
          <p className="text-xs md:text-sm text-gray-500 text-center leading-relaxed w-full md:max-w-[80%]">{t("bookForOtherDesc")}</p>
          
        </button>
      </div>

    </div>
  )
}

